import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Activity, Plus, X, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, ArrowRight, Edit3, Check, Shield, ChevronDown, ChevronUp, Target, Clock, Trash2, Bot, Brain } from "lucide-react";
import { listOpenTrades, createOpenTrade, updateOpenTrade, closeOpenTrade, refreshOpenTrade,
    listPendingOrders, createPendingOrder, updatePendingOrder, cancelPendingOrder,
    adminGetExpertOpenTrades, adminGetExpertPendingOrders } from "../lib/api";
import { AssetAutocomplete } from "../components/AssetAutocomplete";
import { useAuth } from "../lib/auth";

const ACTION_COLOR = {
    "Strong Hold":         { c: "#22c55e", bg: "rgba(34,197,94,0.10)" },
    "Hold":                { c: "#86efac", bg: "rgba(34,197,94,0.06)" },
    "Move Stop Loss":      { c: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
    "Move Take Profit":    { c: "#a855f7", bg: "rgba(168,85,247,0.10)" },
    "Partial Take Profit": { c: "#eab308", bg: "rgba(234,179,8,0.12)" },
    "Close Position":      { c: "#ef4444", bg: "rgba(239,68,68,0.12)" },
};
// Profit Protection action → palette (drives the dominant card colour). Mirrors
// the 4-bucket classification defined in `profit_protection.py`.
const PPS_STYLE = {
    "Strong Hold":               { c: "#22c55e", bg: "rgba(34,197,94,0.12)", emoji: "🟢" },
    "Hold":                      { c: "#86efac", bg: "rgba(34,197,94,0.08)", emoji: "🟢" },
    "Uscita Consigliata":        { c: "#f59e0b", bg: "rgba(245,158,11,0.12)", emoji: "🟠" },
    "Forte Uscita Consigliata":  { c: "#ef4444", bg: "rgba(239,68,68,0.14)", emoji: "🔴" },
};
const PPS_COLOR = (n) => n >= 80 ? "#22c55e" : n >= 60 ? "#86efac" : n >= 40 ? "#f59e0b" : "#ef4444";
const PQS_COLOR = (n) => n >= 70 ? "#22c55e" : n >= 40 ? "#eab308" : "#ef4444";
const REGIME_COLOR = {
    "Strong Bull Trend": "#22c55e", "Bull Trend": "#86efac", "Range Market": "#eab308",
    "High Volatility": "#ef4444", "Bear Trend": "#fca5a5", "Strong Bear Trend": "#ef4444",
};
const WARN_COLOR = { critical: "#ef4444", warn: "#f59e0b", info: "#3b82f6" };

const fmt = (n, dp = 4) => n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp });
const fmtPct = (n) => n == null ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;

export default function OpenTrades() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [scope, setScope] = useState("mine"); // "mine" | "expert" (admin only)
    const [tab, setTab] = useState("positions"); // "positions" | "orders"
    const [trades, setTrades] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState("position"); // "position" | "order"
    const [refreshingId, setRefreshingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            // Admin can flip to a global expert_auto view; regular users always
            // see only their own trades. Endpoints return the same shape.
            const useExpert = isAdmin && scope === "expert";
            const [t, o] = await Promise.all([
                useExpert ? adminGetExpertOpenTrades() : listOpenTrades(),
                useExpert ? adminGetExpertPendingOrders() : listPendingOrders(),
            ]);
            setTrades(t.items || []);
            setOrders(o.items || []);
            // Auto-close notifications: when the backend persisted any
            // trades to closed_positions during this list call, surface a
            // toast for each so the user is aware their SL/TP triggered.
            (t.auto_closed || []).forEach(ac => {
                const label = ac.exit_reason === "tp" ? "Take Profit 🎯"
                            : ac.exit_reason === "sl" ? "Stop Loss 🛑"
                            : "chiusura automatica";
                const pnl = ac.pnl_pct != null ? ` (${ac.pnl_pct > 0 ? "+" : ""}${ac.pnl_pct.toFixed(2)}%)` : "";
                toast.info(`${ac.symbol} chiuso da ${label}${pnl}`);
            });
            // Pending → open trade promotions: notify the user that a
            // limit/stop order has just been filled.
            (o.promoted || []).forEach(p => {
                toast.success(`🎯 Ordine eseguito: ${p.symbol} ${p.side?.toUpperCase()} a $${p.fill_price?.toFixed(2)}`);
            });
            if (o.promoted?.length > 0) {
                // Refetch open trades — the promoted orders just landed there
                const t2 = await listOpenTrades();
                setTrades(t2.items || []);
            }
        } catch (err) {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [scope]);

    const onRefresh = async (id) => {
        setRefreshingId(id);
        try {
            const t = await refreshOpenTrade(id);
            // Auto-close on manual refresh: backend returns _auto_closed=true
            // and won't appear in the open-trades list anymore. Remove from
            // UI and show a toast.
            if (t._auto_closed) {
                const cp = t.closed_position || {};
                const label = cp.exit_reason === "tp" ? "Take Profit 🎯"
                            : cp.exit_reason === "sl" ? "Stop Loss 🛑" : "chiusura";
                const pnl = cp.pnl_pct != null ? ` (${cp.pnl_pct > 0 ? "+" : ""}${cp.pnl_pct.toFixed(2)}%)` : "";
                setTrades(prev => prev.filter(p => p.id !== id));
                toast.success(`${t.symbol} chiuso da ${label}${pnl} — vedi Posizioni Chiuse`);
            } else {
                setTrades(prev => prev.map(p => p.id === id ? t : p));
                toast.success("Analisi aggiornata");
            }
        } catch {
            toast.error("Aggiornamento fallito");
        } finally {
            setRefreshingId(null);
        }
    };

    const onClose = async (id) => {
        if (!confirm("Chiudere questa posizione? L'operazione è irreversibile.")) return;
        try {
            const r = await closeOpenTrade(id);
            setTrades(prev => prev.filter(p => p.id !== id));
            const cp = r?.closed_position;
            if (cp?.pnl_pct != null) {
                toast.success(`Trade chiuso · PnL ${cp.pnl_pct > 0 ? "+" : ""}${cp.pnl_pct.toFixed(2)}% — vedi Posizioni Chiuse`);
            } else {
                toast.success("Trade chiuso — vedi Posizioni Chiuse");
            }
        } catch {
            toast.error("Errore chiusura trade");
        }
    };

    const stats = useMemo(() => {
        const total = trades.length;
        const longs = trades.filter(t => t.side === "long").length;
        const shorts = total - longs;
        const winners = trades.filter(t => (t.current_pnl_pct ?? 0) > 0).length;
        const avgPps = total > 0 ? Math.round(trades.reduce((s, t) => s + (t.pps_score ?? t.pqs_score ?? 0), 0) / total) : 0;
        const warningCount = trades.reduce((s, t) => s + ((t.early_warnings || []).filter(w => w.severity === "critical").length), 0);
        return { total, longs, shorts, winners, avgPps, warningCount };
    }, [trades]);

    return (
        <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto" data-testid="open-trades-page">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5 border-b border-soft pb-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                        <Activity size={22} style={{ color: "var(--brand-blue)" }} />
                        TRADING APERTI
                    </h1>
                    <div className="text-[11px] text-mute mt-1">
                        {scope === "expert"
                            ? "Vista amministratore — trade autonomi del layer Expert (bot)"
                            : "Gestione attiva delle posizioni — analisi continua via AI Trading Analyst"}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <div
                            className="inline-flex border border-soft"
                            data-testid="expert-scope-toggle"
                            role="tablist"
                            aria-label="Ambito visualizzazione trade"
                        >
                            <button
                                type="button"
                                data-testid="scope-mine"
                                onClick={() => setScope("mine")}
                                aria-selected={scope === "mine"}
                                className="px-3 py-2 text-[10px] font-bold tracking-wider transition-colors"
                                style={scope === "mine"
                                    ? { color: "#000", background: "var(--amber)", borderRight: "1px solid var(--amber)" }
                                    : { color: "var(--text-mute)", borderRight: "1px solid var(--border-soft)" }}
                            >
                                I MIEI TRADE
                            </button>
                            <button
                                type="button"
                                data-testid="scope-expert"
                                onClick={() => setScope("expert")}
                                aria-selected={scope === "expert"}
                                className="px-3 py-2 text-[10px] font-bold tracking-wider transition-colors flex items-center gap-1.5"
                                style={scope === "expert"
                                    ? { color: "#000", background: "#a855f7" }
                                    : { color: "var(--text-mute)" }}
                            >
                                <Bot size={11} />
                                TRADE EXPERT AUTO
                            </button>
                        </div>
                    )}
                    {scope === "mine" && (
                        <button
                            data-testid="add-trade-btn"
                            onClick={() => setShowForm(s => !s)}
                            className="border px-4 py-2 text-xs font-semibold tracking-wider hover:bg-elev2 flex items-center gap-2"
                            style={{ color: "var(--amber)", borderColor: "var(--amber)" }}
                        >
                            <Plus size={14} />
                            {showForm ? "ANNULLA" : "NUOVA POSIZIONE"}
                        </button>
                    )}
                </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-4">
                <StatBox label="POSIZIONI APERTE" value={stats.total} />
                <StatBox label="LONG / SHORT" value={`${stats.longs} / ${stats.shorts}`} />
                <StatBox label="IN PROFITTO" value={`${stats.winners} / ${stats.total}`} />
                <StatBox label="PROTECTION MEDIO" value={`${stats.avgPps}/100`} color={PPS_COLOR(stats.avgPps)} />
                <StatBox label="WARNING CRITICI" value={stats.warningCount} color={stats.warningCount > 0 ? "#ef4444" : "#86efac"} />
                <button onClick={load} className="border border-soft text-[10px] text-mute hover:text-foreground py-2 flex items-center justify-center gap-1.5" data-testid="refresh-all-btn">
                    <RefreshCw size={11} />
                    REFRESH
                </button>
            </div>

            {showForm && (
                <TradeForm
                    mode={formMode}
                    onMode={setFormMode}
                    onSaved={(t, wasPending) => {
                        if (wasPending) {
                            // The order was immediately promoted → goes to trades
                            if (t._promoted) setTrades(prev => [t, ...prev]);
                            else setOrders(prev => [t, ...prev]);
                        } else {
                            setTrades(prev => [t, ...prev]);
                        }
                        setShowForm(false);
                        toast.success(wasPending && !t._promoted ? "Ordine registrato" : "Posizione aggiunta");
                    }}
                />
            )}

            {/* Tab switcher: POSIZIONI | ORDINI */}
            <div className="flex gap-1 mb-4 border-b border-soft">
                <button
                    data-testid="tab-positions"
                    onClick={() => setTab("positions")}
                    className={`px-4 py-2 text-xs tracking-widest font-bold border-b-2 transition-colors ${tab === "positions" ? "border-amber-400 text-amber-400" : "border-transparent text-mute hover:text-dim"}`}>
                    <Activity size={12} className="inline mr-1.5" />
                    POSIZIONI ({trades.length})
                </button>
                <button
                    data-testid="tab-orders"
                    onClick={() => setTab("orders")}
                    className={`px-4 py-2 text-xs tracking-widest font-bold border-b-2 transition-colors ${tab === "orders" ? "border-amber-400 text-amber-400" : "border-transparent text-mute hover:text-dim"}`}>
                    <Clock size={12} className="inline mr-1.5" />
                    ORDINI ({orders.length})
                </button>
            </div>

            {loading && <div className="text-mute text-sm py-8 text-center">Caricamento posizioni…</div>}

            {!loading && tab === "positions" && trades.length === 0 && !showForm && (
                <div className="border border-soft p-8 text-center text-mute text-sm" data-testid="empty-trades">
                    <Activity size={20} className="mx-auto mb-2" />
                    <div className="font-semibold mb-1">Nessuna posizione aperta</div>
                    <div className="text-[11px]">
                        Clicca "Nuova posizione" per registrare un trade già aperto. L'AI Trading Analyst monitorerà automaticamente
                        la qualità del setup e ti suggerirà le azioni operative.
                    </div>
                </div>
            )}

            {!loading && tab === "orders" && orders.length === 0 && !showForm && (
                <div className="border border-soft p-8 text-center text-mute text-sm" data-testid="empty-orders">
                    <Clock size={20} className="mx-auto mb-2" />
                    <div className="font-semibold mb-1">Nessun ordine programmato</div>
                    <div className="text-[11px]">
                        Clicca "Nuova posizione" → "ORDINE" per registrare un ordine limit/stop. Verrà eseguito automaticamente
                        quando il prezzo raggiungerà il livello di trigger.
                    </div>
                </div>
            )}

            {tab === "positions" && (
                <div className="space-y-3" data-testid="positions-list">
                    {trades.map(t => (
                        <TradeCard
                            key={t.id}
                            trade={t}
                            onRefresh={() => onRefresh(t.id)}
                            onClose={() => onClose(t.id)}
                            refreshing={refreshingId === t.id}
                            onUpdated={(upd) => setTrades(prev => prev.map(p => p.id === t.id ? upd : p))}
                            readOnly={scope === "expert"}
                        />
                    ))}
                </div>
            )}

            {tab === "orders" && (
                <div className="space-y-2" data-testid="orders-list">
                    {orders.map(o => (
                        <OrderCard
                            key={o.id}
                            order={o}
                            onCancel={async () => {
                                if (!window.confirm("Annullare questo ordine?")) return;
                                try {
                                    await cancelPendingOrder(o.id);
                                    setOrders(prev => prev.filter(p => p.id !== o.id));
                                    toast.success("Ordine annullato");
                                } catch { toast.error("Errore annullamento"); }
                            }}
                            onUpdated={(upd) => setOrders(prev => prev.map(p => p.id === o.id ? upd : p))}
                            readOnly={scope === "expert"}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

const StatBox = ({ label, value, color }) => (
    <div className="border border-soft bg-elev px-3 py-2">
        <div className="text-[9px] tracking-widest text-mute">{label}</div>
        <div className="text-base font-bold tnum" style={color ? { color } : undefined}>{value}</div>
    </div>
);

// Tiny inline SVG sparkline for the PQS-history evolution. Pure presentation —
// receives an array of 0-100 ints and draws a connected polyline coloured by
// the trend (green improving, red deteriorating, neutral otherwise). Designed
// to render dozens of cards without re-layout cost.
const PqsSparkline = ({ values }) => {
    if (!values || values.length < 2) {
        return <div className="text-[10px] text-dim italic">Storico in costruzione…</div>;
    }
    const W = 200, H = 28, PAD = 2;
    const min = 0, max = 100;
    const stepX = (W - PAD * 2) / (values.length - 1);
    const points = values.map((v, i) => {
        const x = PAD + i * stepX;
        const y = H - PAD - ((v - min) / (max - min)) * (H - PAD * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    const last = values[values.length - 1];
    const first = values[0];
    const stroke = last - first >= 8 ? "#22c55e" : last - first <= -8 ? "#ef4444" : "#86efac";
    return (
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
            <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
            {values.map((v, i) => {
                const x = PAD + i * stepX;
                const y = H - PAD - ((v - min) / (max - min)) * (H - PAD * 2);
                return <circle key={i} cx={x} cy={y} r={i === values.length - 1 ? 2.5 : 1.5} fill={stroke} />;
            })}
        </svg>
    );
};

function TradeForm({ mode = "position", onMode, onSaved }) {
    const [symbol, setSymbol] = useState("");
    const [name, setName] = useState("");
    const [side, setSide] = useState("long");
    const [entry, setEntry] = useState("");
    const [qty, setQty] = useState("1");
    const [sl, setSl] = useState("");
    const [tp, setTp] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const isOrder = mode === "order";

    const submit = async (e) => {
        e.preventDefault();
        if (!symbol || !entry) {
            toast.error(isOrder
                ? "Compila simbolo e prezzo di TRIGGER"
                : "Compila simbolo e prezzo di entrata");
            return;
        }
        setSubmitting(true);
        try {
            if (isOrder) {
                const o = await createPendingOrder({
                    symbol: symbol.toUpperCase().trim(),
                    name: name || symbol.toUpperCase(),
                    side,
                    trigger_price: parseFloat(entry),
                    quantity: parseFloat(qty || "1"),
                    stop_loss: sl ? parseFloat(sl) : null,
                    take_profit: tp ? parseFloat(tp) : null,
                    notes,
                });
                onSaved(o, true);
            } else {
                const t = await createOpenTrade({
                    symbol: symbol.toUpperCase().trim(),
                    name: name || symbol.toUpperCase(),
                    side,
                    entry_price: parseFloat(entry),
                    quantity: parseFloat(qty || "1"),
                    stop_loss: sl ? parseFloat(sl) : null,
                    take_profit: tp ? parseFloat(tp) : null,
                    notes,
                });
                onSaved(t, false);
            }
        } catch (err) {
            toast.error("Errore: " + (err?.response?.data?.detail || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="trade-form" className="border bg-elev p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3" style={{ borderColor: "var(--amber)" }}>
            {/* Mode selector — POSIZIONE GIÀ APERTA vs ORDINE PROGRAMMATO */}
            <div className="md:col-span-12 flex gap-2 border-b border-soft pb-3">
                <button type="button"
                    onClick={() => onMode?.("position")}
                    data-testid="mode-position"
                    className={`flex-1 py-2 text-xs font-bold tracking-wider border ${!isOrder ? "" : "text-mute"}`}
                    style={!isOrder
                        ? { color: "var(--amber)", borderColor: "var(--amber)", background: "rgba(245,158,11,0.10)" }
                        : { borderColor: "var(--border-soft)" }}>
                    <Activity size={11} className="inline mr-1.5" />
                    POSIZIONE GIÀ APERTA
                </button>
                <button type="button"
                    onClick={() => onMode?.("order")}
                    data-testid="mode-order"
                    className={`flex-1 py-2 text-xs font-bold tracking-wider border ${isOrder ? "" : "text-mute"}`}
                    style={isOrder
                        ? { color: "#a855f7", borderColor: "#a855f7", background: "rgba(168,85,247,0.10)" }
                        : { borderColor: "var(--border-soft)" }}>
                    <Clock size={11} className="inline mr-1.5" />
                    ORDINE PROGRAMMATO
                </button>
            </div>
            <div className="md:col-span-4">
                <label className="text-[10px] text-mute tracking-widest block mb-1">SIMBOLO *</label>
                <AssetAutocomplete
                    value={symbol}
                    onChange={(v) => setSymbol(v)}
                    onPick={(item) => { setSymbol(item.symbol); setName(item.name || ""); }}
                    placeholder="BTC-USD, TSLA, EURUSD=X…"
                />
            </div>
            <div className="md:col-span-4">
                <label className="text-[10px] text-mute tracking-widest block mb-1">NOME</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm" placeholder="Bitcoin" />
            </div>
            <div className="md:col-span-2">
                <label className="text-[10px] text-mute tracking-widest block mb-1">TIPO *</label>
                <div className="flex gap-1">
                    <button type="button" onClick={() => setSide("long")} data-testid="side-long"
                        className={`flex-1 py-1.5 text-xs font-bold border ${side === "long" ? "" : "text-mute"}`}
                        style={side === "long" ? { color: "#22c55e", borderColor: "#22c55e", background: "rgba(34,197,94,0.10)" } : { borderColor: "var(--border-soft)" }}>
                        LONG
                    </button>
                    <button type="button" onClick={() => setSide("short")} data-testid="side-short"
                        className={`flex-1 py-1.5 text-xs font-bold border ${side === "short" ? "" : "text-mute"}`}
                        style={side === "short" ? { color: "#ef4444", borderColor: "#ef4444", background: "rgba(239,68,68,0.10)" } : { borderColor: "var(--border-soft)" }}>
                        SHORT
                    </button>
                </div>
            </div>
            <div className="md:col-span-2">
                <label className="text-[10px] text-mute tracking-widest block mb-1">QUANTITÀ</label>
                <input type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum" />
            </div>
            <div className="md:col-span-3">
                <label className="text-[10px] text-mute tracking-widest block mb-1">
                    {isOrder ? "PREZZO TRIGGER *" : "PREZZO ENTRATA *"}
                </label>
                <input data-testid="entry-price" type="number" step="any" value={entry}
                    onChange={(e) => setEntry(e.target.value)}
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum"
                    placeholder={isOrder ? "Es. 170.00 (buy dip) / 220.00 (breakout)" : "0.00"} />
                {isOrder && (
                    <div className="text-[9px] text-mute mt-1">
                        L'ordine si attiverà quando il prezzo raggiungerà questo livello
                    </div>
                )}
            </div>
            <div className="md:col-span-3">
                <label className="text-[10px] text-mute tracking-widest block mb-1">STOP LOSS</label>
                <input data-testid="stop-loss" type="number" step="any" value={sl} onChange={(e) => setSl(e.target.value)} className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum" placeholder="0.00" />
            </div>
            <div className="md:col-span-3">
                <label className="text-[10px] text-mute tracking-widest block mb-1">TAKE PROFIT</label>
                <input data-testid="take-profit" type="number" step="any" value={tp} onChange={(e) => setTp(e.target.value)} className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum" placeholder="0.00" />
            </div>
            <div className="md:col-span-3 flex items-end">
                <button type="submit" disabled={submitting} data-testid="submit-trade"
                    className="w-full border py-1.5 text-xs font-bold tracking-wider disabled:opacity-50"
                    style={{
                        background: isOrder ? "#a855f7" : "var(--amber)",
                        color: "#000",
                        borderColor: isOrder ? "#a855f7" : "var(--amber)",
                    }}>
                    {submitting
                        ? (isOrder ? "REGISTRAZIONE…" : "AGGIUNTA…")
                        : (isOrder ? "REGISTRA ORDINE" : "AGGIUNGI POSIZIONE")}
                </button>
            </div>
            <div className="md:col-span-12">
                <label className="text-[10px] text-mute tracking-widest block mb-1">NOTE (opzionale)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-elev2 border border-soft px-2 py-1.5 text-xs" placeholder="Ragione del trade, strategia…" />
            </div>
        </form>
    );
}

/* ----------- OrderCard — single pending order row ----------- */
function OrderCard({ order, onCancel, onUpdated, readOnly = false }) {
    const isLong = (order.side || "").toLowerCase() === "long";
    const Icon = isLong ? TrendingUp : TrendingDown;
    const trigger = Number(order.trigger_price || 0);
    const lastSeen = order.last_seen_price != null ? Number(order.last_seen_price) : null;
    const direction = order.trigger_direction || "below";
    const distance = lastSeen != null && trigger > 0
        ? ((trigger - lastSeen) / lastSeen * 100)
        : null;
    const distColor = distance == null ? "var(--text-mute)"
        : Math.abs(distance) < 1 ? "#f59e0b"   // close to trigger
        : "var(--text-dim)";

    const [editing, setEditing] = useState(false);
    const [trgDraft, setTrgDraft] = useState(trigger);
    const [slDraft, setSlDraft] = useState(order.stop_loss ?? "");
    const [tpDraft, setTpDraft] = useState(order.take_profit ?? "");

    useEffect(() => { setTrgDraft(order.trigger_price ?? ""); }, [order.trigger_price]);
    useEffect(() => { setSlDraft(order.stop_loss ?? ""); }, [order.stop_loss]);
    useEffect(() => { setTpDraft(order.take_profit ?? ""); }, [order.take_profit]);

    const save = async () => {
        try {
            const upd = await updatePendingOrder(order.id, {
                trigger_price: parseFloat(String(trgDraft).replace(",", ".")),
                stop_loss: slDraft === "" ? null : parseFloat(String(slDraft).replace(",", ".")),
                take_profit: tpDraft === "" ? null : parseFloat(String(tpDraft).replace(",", ".")),
            });
            onUpdated(upd);
            setEditing(false);
            toast.success("Ordine aggiornato");
        } catch { toast.error("Errore aggiornamento ordine"); }
    };

    return (
        <div
            data-testid={`order-card-${order.symbol}`}
            className="border border-soft bg-elev p-3"
            style={{ borderLeftColor: "#a855f7", borderLeftWidth: 3 }}>
            <div className="grid grid-cols-12 gap-3 items-center">
                {/* Symbol + side */}
                <div className="col-span-2 flex items-center gap-2">
                    <Icon size={14} style={{ color: isLong ? "#22c55e" : "#ef4444" }} />
                    <div>
                        <div className="font-bold text-sm" style={{ color: "var(--brand-blue)" }}>{order.symbol}</div>
                        <div className="text-[9px] uppercase text-mute">{order.side} · {direction === "below" ? "↓ DIP" : "↑ BREAKOUT"}</div>
                    </div>
                </div>
                {/* Trigger */}
                <div className="col-span-2 tnum">
                    <div className="text-[9px] tracking-widest text-mute">TRIGGER</div>
                    {editing ? (
                        <input type="number" step="any" value={trgDraft}
                            onChange={(e) => setTrgDraft(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-1 py-0.5 text-sm tnum"
                            data-testid={`order-trigger-input-${order.symbol}`} />
                    ) : (
                        <div className="text-sm font-bold" style={{ color: "#a855f7" }}>${fmt(trigger)}</div>
                    )}
                </div>
                {/* Last seen + distance */}
                <div className="col-span-2 tnum">
                    <div className="text-[9px] tracking-widest text-mute">PREZZO ATTUALE</div>
                    <div className="text-sm">{lastSeen != null ? `$${fmt(lastSeen)}` : "—"}</div>
                    {distance != null && (
                        <div className="text-[10px]" style={{ color: distColor }}>
                            {distance > 0 ? "↑" : "↓"} {Math.abs(distance).toFixed(2)}% al trigger
                        </div>
                    )}
                </div>
                {/* SL */}
                <div className="col-span-2 tnum">
                    <div className="text-[9px] tracking-widest text-mute">STOP LOSS</div>
                    {editing ? (
                        <input type="number" step="any" value={slDraft}
                            onChange={(e) => setSlDraft(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-1 py-0.5 text-sm tnum" />
                    ) : (
                        <div className="text-sm font-bold" style={{ color: "#ef4444" }}>{order.stop_loss != null ? `$${fmt(order.stop_loss)}` : "—"}</div>
                    )}
                </div>
                {/* TP */}
                <div className="col-span-2 tnum">
                    <div className="text-[9px] tracking-widest text-mute">TAKE PROFIT</div>
                    {editing ? (
                        <input type="number" step="any" value={tpDraft}
                            onChange={(e) => setTpDraft(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-1 py-0.5 text-sm tnum" />
                    ) : (
                        <div className="text-sm font-bold" style={{ color: "#22c55e" }}>{order.take_profit != null ? `$${fmt(order.take_profit)}` : "—"}</div>
                    )}
                </div>
                {/* Actions */}
                <div className="col-span-2 flex items-center justify-end gap-2">
                    {readOnly ? (
                        <span className="text-[9px] tracking-widest px-2 py-1 border" style={{ color: "#a855f7", borderColor: "#a855f7" }}>
                            <Bot size={9} className="inline mr-1" />BOT
                        </span>
                    ) : editing ? (
                        <>
                            <button onClick={save} data-testid={`order-save-${order.symbol}`} className="text-green-500 hover:text-green-400">
                                <Check size={14} />
                            </button>
                            <button onClick={() => setEditing(false)} className="text-mute hover:text-dim">
                                <X size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setEditing(true)} data-testid={`order-edit-${order.symbol}`} className="text-mute hover:text-dim">
                                <Edit3 size={12} />
                            </button>
                            <button onClick={onCancel} data-testid={`order-cancel-${order.symbol}`} className="text-mute hover:text-red-400">
                                <Trash2 size={12} />
                            </button>
                        </>
                    )}
                </div>
            </div>
            {order.notes && (
                <div className="text-[10px] text-dim mt-2 italic">{order.notes}</div>
            )}
        </div>
    );
}

function TradeCard({ trade, onRefresh, onClose, refreshing, onUpdated, readOnly = false }) {
    const isLong = trade.side === "long";
    const accent = isLong ? "#22c55e" : "#ef4444";
    const pnl = trade.current_pnl_pct;
    const Icon = isLong ? TrendingUp : TrendingDown;
    // Profit Protection — primary recommendation driver (user choice C).
    // Legacy Trade Management AI (TMS) is preserved untouched and exposed
    // under the collapsible "Dettaglio analisi" block below.
    const ppsScore = trade.pps_score;
    const ppsAction = trade.pps_action || "Hold";
    const ppsStyle = PPS_STYLE[ppsAction] || PPS_STYLE["Hold"];
    const ppsHistory = trade.pqs_history || [];
    const warnings = trade.early_warnings || [];
    const exitConf = trade.exit_confidence;
    const trailing = trade.trailing_stop;
    const trailingStructural = trade.trailing_stop_structural;
    const pqsTrend = trade.pqs_trend || {};
    // Modifica 11 — Trend Structure Integrity surfaced on each trade card.
    const trendStruct = trade.trend_structure;
    // Colorazione relativa al lato del trade (richiesta utente 12 feb '26):
    //   LONG  + HH/HL INTATTA → verde (favorevole)
    //   LONG  + LH/LL INTATTA → rosso (contrasta)
    //   SHORT + LH/LL INTATTA → verde (favorevole)
    //   SHORT + HH/HL INTATTA → rosso (contrasta)
    //   STRUTTURA MISTA / sconosciuta → grigio neutro
    const _structLabel = trendStruct?.structure_label;
    const _sideLong = (trade.side || "").toLowerCase() === "long";
    const trendStructColor = (() => {
        if (_structLabel === "HH/HL INTATTA") return _sideLong ? "#22c55e" : "#ef4444";
        if (_structLabel === "LH/LL INTATTA") return _sideLong ? "#ef4444" : "#22c55e";
        return "#9ca3af";
    })();
    const tsiFloorApplied = trade.tsi_floor_applied === true;
    // Section 3 Part C — Exit review recommendation persisted on the trade.
    // Rendered as an informational strip; does NOT close the position
    // unless EXPERT_EXIT_REVIEW_AUTO_ACT is enabled (default off).
    const exitReview = trade.exit_review;

    // Legacy TMS fields — kept for the "Dettaglio analisi" advanced section.
    const tmsAction = trade.primary_action || "Hold";
    const tmsStyle = ACTION_COLOR[tmsAction] || ACTION_COLOR["Hold"];
    const pqs = trade.pqs_score;
    const regime = trade.market_regime;

    const [editingSL, setEditingSL] = useState(false);
    const [editingTP, setEditingTP] = useState(false);
    const [slDraft, setSlDraft] = useState(trade.stop_loss ?? "");
    const [tpDraft, setTpDraft] = useState(trade.take_profit ?? "");
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Re-sync drafts when the underlying trade prop changes (e.g. after a
    // refresh or after a save returns updated values). Without this, the
    // useState initialisers from first mount would keep stale drafts forever.
    useEffect(() => { setSlDraft(trade.stop_loss ?? ""); }, [trade.stop_loss]);
    useEffect(() => { setTpDraft(trade.take_profit ?? ""); }, [trade.take_profit]);

    const saveField = async (field, value) => {
        try {
            // Allow Italian "165,5" notation by normalising commas to dots
            const raw = String(value ?? "").trim().replace(",", ".");
            if (raw === "") { toast.error("Valore vuoto"); return; }
            const v = parseFloat(raw);
            if (Number.isNaN(v) || !Number.isFinite(v) || v <= 0) {
                toast.error("Valore non valido"); return;
            }
            // Skip the save when nothing actually changed — saves a roundtrip
            const cur = field === "stop_loss" ? trade.stop_loss : trade.take_profit;
            if (cur != null && Math.abs(Number(cur) - v) < 1e-9) {
                setEditingSL(false); setEditingTP(false); return;
            }
            const upd = await updateOpenTrade(trade.id, { [field]: v });
            onUpdated(upd);
            toast.success(`${field === "stop_loss" ? "Stop Loss" : "Take Profit"} aggiornato a $${v}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore aggiornamento");
        } finally {
            setEditingSL(false);
            setEditingTP(false);
        }
    };

    // Save when the user presses Enter / Tab inside the input — the most
    // common UX expectation. Esc cancels and reverts the draft.
    const handleSlKey = (e) => {
        if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); saveField("stop_loss", slDraft); }
        else if (e.key === "Escape") { setSlDraft(trade.stop_loss ?? ""); setEditingSL(false); }
    };
    const handleTpKey = (e) => {
        if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); saveField("take_profit", tpDraft); }
        else if (e.key === "Escape") { setTpDraft(trade.take_profit ?? ""); setEditingTP(false); }
    };

    // Apply the AI-suggested trailing stop with a single click. Updates DB +
    // local state — no auto-execution: the user clicked, the user committed.
    const acceptTrailingStop = async () => {
        if (!trailing?.suggested_stop_loss) return;
        try {
            const upd = await updateOpenTrade(trade.id, { stop_loss: trailing.suggested_stop_loss });
            onUpdated(upd);
            toast.success(`Stop Loss spostato a $${fmt(trailing.suggested_stop_loss)}`);
        } catch {
            toast.error("Errore aggiornamento Stop Loss");
        }
    };

    // Modifica 11 — same single-click commit, but using the structural
    // EQH/EQL trailing stop (tiered by PPS: aggressivo / standard / conservativo).
    const acceptStructuralTrailing = async () => {
        if (!trailingStructural?.suggested_stop_loss) return;
        try {
            const upd = await updateOpenTrade(trade.id, { stop_loss: trailingStructural.suggested_stop_loss });
            onUpdated(upd);
            toast.success(`Stop Loss strutturale (${trailingStructural.tier}) → $${fmt(trailingStructural.suggested_stop_loss)}`);
        } catch {
            toast.error("Errore aggiornamento Stop Loss");
        }
    };

    return (
        <div className="border border-soft bg-elev" data-testid={`trade-card-${trade.symbol}`}>
            {/* Header row */}
            <div className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 border-b border-soft">
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest px-2 py-0.5 border"
                        style={{ color: accent, borderColor: accent, background: `${accent}10` }}>
                        <Icon size={11} />{trade.side.toUpperCase()}
                    </span>
                    <div>
                        <div className="font-bold text-sm tracking-wide" style={{ color: "var(--brand-blue)" }}>{trade.symbol}</div>
                        <div className="text-[10px] text-mute truncate max-w-xs" title={trade.name}>{trade.name}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {readOnly && (
                        <span className="text-[9px] tracking-widest px-1.5 py-0.5 border flex items-center gap-1" style={{ color: "#a855f7", borderColor: "#a855f7" }}>
                            <Bot size={9} />EXPERT AUTO
                        </span>
                    )}
                    {!readOnly && (
                        <>
                            <button onClick={onRefresh} disabled={refreshing} className="text-mute hover:text-foreground p-1 disabled:opacity-50" title="Aggiorna analisi" data-testid={`refresh-${trade.symbol}`}>
                                <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                            </button>
                            <button onClick={onClose} className="text-mute hover:text-red-500 p-1" title="Chiudi posizione" data-testid={`close-${trade.symbol}`}>
                                <X size={14} />
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 p-3">
                {/* Price block */}
                <div className="lg:col-span-3 space-y-2">
                    <div>
                        <div className="text-[9px] tracking-widest text-mute">ENTRATA → ATTUALE</div>
                        <div className="text-xs tnum text-mute">${fmt(trade.entry_price)}</div>
                        <div className="text-xl font-bold tnum" style={{ color: accent }}>${fmt(trade.current_price)}</div>
                    </div>
                    <div className="border-t border-soft pt-1.5">
                        <div className="text-[9px] tracking-widest text-mute">P/L</div>
                        <div className="text-lg font-bold tnum" style={{ color: pnl >= 0 ? "#22c55e" : "#ef4444" }}>
                            {fmtPct(pnl)}
                        </div>
                    </div>
                </div>

                {/* Stop / Target */}
                <div className="lg:col-span-3 space-y-2">
                    <div>
                        <div className="flex items-center justify-between text-[9px] tracking-widest text-mute">
                            <span>STOP LOSS</span>
                            {!readOnly && !editingSL && (
                                <button onClick={() => setEditingSL(true)} className="text-mute hover:text-foreground" data-testid={`edit-sl-${trade.symbol}`}><Edit3 size={10} /></button>
                            )}
                            {!readOnly && editingSL && (
                                <button onClick={() => saveField("stop_loss", slDraft)} className="text-green-500"><Check size={12} /></button>
                            )}
                        </div>
                        {editingSL && !readOnly ? (
                            <input autoFocus type="number" step="any" value={slDraft}
                                onChange={(e) => setSlDraft(e.target.value)}
                                onKeyDown={handleSlKey}
                                onBlur={() => saveField("stop_loss", slDraft)}
                                data-testid={`sl-input-${trade.symbol}`}
                                className="w-full bg-elev2 border border-soft px-1 py-0.5 text-sm tnum" />
                        ) : (
                            <div className="tnum font-bold" style={{ color: "#ef4444" }}>${fmt(trade.stop_loss)}</div>
                        )}
                    </div>
                    <div className="border-t border-soft pt-1.5">
                        <div className="flex items-center justify-between text-[9px] tracking-widest text-mute">
                            <span>TAKE PROFIT</span>
                            {!readOnly && !editingTP && (
                                <button onClick={() => setEditingTP(true)} className="text-mute hover:text-foreground" data-testid={`edit-tp-${trade.symbol}`}><Edit3 size={10} /></button>
                            )}
                            {!readOnly && editingTP && (
                                <button onClick={() => saveField("take_profit", tpDraft)} className="text-green-500"><Check size={12} /></button>
                            )}
                        </div>
                        {editingTP && !readOnly ? (
                            <input autoFocus type="number" step="any" value={tpDraft}
                                onChange={(e) => setTpDraft(e.target.value)}
                                onKeyDown={handleTpKey}
                                onBlur={() => saveField("take_profit", tpDraft)}
                                data-testid={`tp-input-${trade.symbol}`}
                                className="w-full bg-elev2 border border-soft px-1 py-0.5 text-sm tnum" />
                        ) : (
                            <div className="tnum font-bold" style={{ color: "#22c55e" }}>${fmt(trade.take_profit)}</div>
                        )}
                        {trade.distance_to_tp_pct != null && (
                            <div className="text-[9px] text-mute mt-0.5">a {fmt(trade.distance_to_tp_pct, 2)}% dal TP</div>
                        )}
                    </div>
                </div>

                {/* PROFIT PROTECTION — primary recommendation block */}
                <div className="lg:col-span-6" data-testid={`pps-${trade.symbol}`}
                    style={{ border: `1px solid ${ppsStyle.c}40`, background: ppsStyle.bg, padding: "10px 12px" }}>
                    <div className="flex items-start justify-between gap-3 mb-1.5">
                        <div className="flex items-center gap-1.5">
                            <Shield size={12} style={{ color: ppsStyle.c }} />
                            <span className="text-[9px] tracking-widest text-mute uppercase">Profit Protection</span>
                        </div>
                        <div className="flex items-end gap-1">
                            <span className="text-3xl font-bold tnum leading-none" style={{ color: PPS_COLOR(ppsScore ?? 50) }}>{ppsScore ?? "—"}</span>
                            <span className="text-[9px] text-mute pb-1">/100</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-xs font-bold tracking-wide" style={{ color: ppsStyle.c }}>
                            {ppsStyle.emoji} {ppsAction.toUpperCase()}
                        </span>
                    </div>
                    <div className="text-[10px] text-dim leading-relaxed mb-2">{trade.pps_narrative || "—"}</div>

                    {/* Section 3 Part C — Exit Review recommendation (Feb 2026)
                        Informational strip. Does NOT auto-close unless the
                        `EXPERT_EXIT_REVIEW_AUTO_ACT` flag is on. */}
                    {exitReview && (
                        <div
                            data-testid={`exit-review-${trade.symbol}`}
                            className="border-t border-soft pt-1.5 mb-2"
                            style={{
                                borderColor:
                                    exitReview.exit_recommendation === "exit" ? "#ef4444" :
                                    exitReview.exit_recommendation === "tighten_stop" ? "#f97316" :
                                    "#22c55e",
                            }}>
                            <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[9px] tracking-widest text-mute">
                                    <Brain size={9} className="inline mr-1" style={{ color: "#a855f7" }} />
                                    EXPERT EXIT REVIEW
                                </span>
                                <span className="text-[10px] font-bold" style={{
                                    color: exitReview.exit_recommendation === "exit" ? "#ef4444" :
                                            exitReview.exit_recommendation === "tighten_stop" ? "#f97316" :
                                            "#22c55e",
                                }}>
                                    {exitReview.exit_recommendation === "exit" && "🚪 ESCI"}
                                    {exitReview.exit_recommendation === "tighten_stop" && "🛡 STRINGI STOP"}
                                    {exitReview.exit_recommendation === "hold" && "✋ TIENI"}
                                    <span className="text-[9px] text-mute ml-1">conv {exitReview.exit_conviction}/5</span>
                                </span>
                            </div>
                            {(exitReview.exit_reasoning || []).slice(0, 2).map((r, i) => (
                                <div key={i} className="text-[10px] text-dim leading-relaxed">
                                    • {r}
                                </div>
                            ))}
                            {exitReview.exit_review_agrees_with_pqs && exitReview.exit_review_agrees_with_pqs !== "not_applicable" && (
                                <div className="text-[9px] text-mute mt-0.5">
                                    vs PPS ({exitReview.pps_at_review}): {
                                        exitReview.exit_review_agrees_with_pqs === "accord" ? "accordo" :
                                        exitReview.exit_review_agrees_with_pqs === "partial" ? "accordo parziale" :
                                        exitReview.exit_review_agrees_with_pqs === "disaccord" ? "disaccordo — Expert override" :
                                        exitReview.exit_review_agrees_with_pqs
                                    }
                                </div>
                            )}
                        </div>
                    )}

                    {/* PQS history sparkline + last 5 numbers */}
                    {ppsHistory.length > 0 && (
                        <div className="border-t border-soft pt-1.5 mb-2" data-testid={`pqs-history-${trade.symbol}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] tracking-widest text-mute">EVOLUZIONE SETUP (PQS)</span>
                                {pqsTrend.label === "improving" && <span className="text-[9px] font-bold" style={{ color: "#22c55e" }}>↑ MIGLIORANDO</span>}
                                {pqsTrend.label === "deteriorating" && <span className="text-[9px] font-bold" style={{ color: "#ef4444" }}>↓ DETERIORANDOSI</span>}
                                {pqsTrend.label === "stable" && <span className="text-[9px] font-bold text-mute">→ STABILE</span>}
                            </div>
                            <PqsSparkline values={ppsHistory} />
                            <div className="text-[10px] tnum text-dim mt-0.5 flex gap-1.5 flex-wrap">
                                {ppsHistory.slice(-5).map((v, i, arr) => (
                                    <span key={i} style={{ color: PQS_COLOR(v), fontWeight: i === arr.length - 1 ? 700 : 400 }}>{v}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Early warnings */}
                    {warnings.length > 0 && (
                        <div className="border-t border-soft pt-1.5 mb-2" data-testid={`warnings-${trade.symbol}`}>
                            <div className="text-[9px] tracking-widest text-mute mb-1">EARLY WARNINGS</div>
                            <div className="space-y-0.5">
                                {warnings.slice(0, 3).map((w, i) => (
                                    <div key={i} className="text-[10px] flex items-start gap-1 leading-snug"
                                        style={{ color: WARN_COLOR[w.severity] || "var(--text-dim)" }}>
                                        <AlertTriangle size={9} className="mt-0.5 flex-shrink-0" />
                                        <span>{w.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Exit confidence (only shown when PPS < 60 → backend already gates it) */}
                    {exitConf && (
                        <div className="border-t border-soft pt-1.5 mb-2" data-testid={`exit-confidence-${trade.symbol}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] tracking-widest text-mute">EXIT CONFIDENCE</span>
                                <span className="text-sm font-bold tnum" style={{ color: PPS_COLOR(100 - exitConf.score) }}>
                                    {exitConf.score}/100
                                </span>
                            </div>
                            <div className="h-1 bg-elev2 relative overflow-hidden mb-1">
                                <div className="absolute inset-y-0 left-0" style={{ width: `${exitConf.score}%`, background: "#ef4444" }} />
                            </div>
                            {exitConf.reasons.slice(0, 3).map((r, i) => (
                                <div key={i} className="text-[10px] text-dim leading-snug">• {r}</div>
                            ))}
                        </div>
                    )}

                    {/* Trailing stop suggestion (only when in profit AND PPS ≥ 60, backend-gated) */}
                    {trailing?.suggested_stop_loss != null && (
                        <div className="border-t border-soft pt-1.5" data-testid={`trailing-stop-${trade.symbol}`}>
                            <div className="text-[9px] tracking-widest text-mute mb-1 flex items-center gap-1">
                                <Target size={9} /> TRAILING STOP AI
                            </div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="text-[10px] text-mute">
                                    Da <span className="tnum text-dim">${fmt(trailing.current_stop_loss || trade.stop_loss)}</span>
                                    {" "}→{" "}
                                    <span className="tnum font-bold" style={{ color: "#22c55e" }}>${fmt(trailing.suggested_stop_loss)}</span>
                                    {trailing.improvement_pct != null && (
                                        <span className="text-[9px] ml-1" style={{ color: "#22c55e" }}>(+{trailing.improvement_pct}%)</span>
                                    )}
                                </div>
                                <button
                                    onClick={acceptTrailingStop}
                                    data-testid={`accept-trailing-${trade.symbol}`}
                                    className="text-[9px] font-bold tracking-wider px-2 py-1 border hover:bg-elev2"
                                    style={{ color: "#22c55e", borderColor: "#22c55e" }}
                                >APPLICA</button>
                            </div>
                            <div className="text-[10px] text-dim leading-snug">{trailing.rationale}</div>
                        </div>
                    )}

                    {/* Modifica 11 — Structural Trailing Stop (EQH/EQL based) */}
                    {trailingStructural?.suggested_stop_loss != null && (
                        <div className="border-t border-soft pt-1.5" data-testid={`trailing-structural-${trade.symbol}`}>
                            <div className="text-[9px] tracking-widest text-mute mb-1 flex items-center gap-1">
                                <Target size={9} /> TRAILING STOP STRUTTURALE
                                <span className="ml-1 px-1.5 py-0.5 border text-[8px] font-bold tracking-wider"
                                    style={{ color: "#a855f7", borderColor: "#a855f7" }}>
                                    {trailingStructural.tier}
                                </span>
                            </div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="text-[10px] text-mute">
                                    Da <span className="tnum text-dim">${fmt(trailingStructural.current_stop_loss || trade.stop_loss)}</span>
                                    {" "}→{" "}
                                    <span className="tnum font-bold" style={{ color: "#a855f7" }}>${fmt(trailingStructural.suggested_stop_loss)}</span>
                                </div>
                                <button
                                    onClick={acceptStructuralTrailing}
                                    data-testid={`accept-trailing-structural-${trade.symbol}`}
                                    className="text-[9px] font-bold tracking-wider px-2 py-1 border hover:bg-elev2"
                                    style={{ color: "#a855f7", borderColor: "#a855f7" }}
                                >APPLICA</button>
                            </div>
                            {trailingStructural.basis && (
                                <div className="text-[10px] text-dim leading-snug">{trailingStructural.basis}</div>
                            )}
                        </div>
                    )}

                    {/* Modifica 11 — Trend Structure Integrity badge */}
                    {trendStruct?.structure_label && (
                        <div className="border-t border-soft pt-1.5" data-testid={`trend-structure-${trade.symbol}`}>
                            <div className="text-[9px] tracking-widest text-mute mb-1">TREND STRUCTURE</div>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-xs font-bold tracking-wide" style={{ color: trendStructColor }}>
                                    {trendStruct.structure_label}
                                </span>
                                {trendStruct.tsi_multiplier != null && (
                                    <span className="text-[9px] text-mute">TSI×{trendStruct.tsi_multiplier}</span>
                                )}
                            </div>
                            {trendStruct.correction_label && (
                                <div className="text-[10px] text-dim mt-0.5">{trendStruct.correction_label}</div>
                            )}
                            {tsiFloorApplied && (
                                <div className="text-[9px] mt-1" style={{ color: "#86efac" }}>
                                    ✓ PQS floor {trade.tsi_floor_value} applicato — correzione normale entro trend
                                </div>
                            )}
                            {trendStruct.reversal_volume === true && (
                                <div className="text-[9px] mt-1" style={{ color: "#f59e0b" }}>
                                    ⚠ Volumi di inversione presenti
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Advanced details — collapsible: legacy PQS + Trade Management AI */}
            <div className="border-t border-soft">
                <button
                    onClick={() => setShowAdvanced(s => !s)}
                    data-testid={`toggle-advanced-${trade.symbol}`}
                    className="w-full px-3 py-1.5 text-[9px] tracking-widest text-mute hover:text-foreground flex items-center justify-center gap-1.5 hover:bg-elev2"
                >
                    {showAdvanced ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    {showAdvanced ? "NASCONDI DETTAGLIO ANALISI" : "DETTAGLIO ANALISI AVANZATA (PQS + TRADE MANAGEMENT)"}
                </button>
                {showAdvanced && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-3 border-t border-soft">
                        {/* PQS detail */}
                        <div data-testid={`pqs-detail-${trade.symbol}`}>
                            <div className="text-[9px] tracking-widest text-mute mb-0.5">POSITION QUALITY SCORE</div>
                            <div className="flex items-end gap-2">
                                <span className="text-2xl font-bold tnum" style={{ color: PQS_COLOR(pqs ?? 50) }}>{pqs ?? "—"}</span>
                                <span className="text-[9px] text-mute pb-1">/100</span>
                            </div>
                            <div className="h-1 bg-elev2 relative overflow-hidden mt-1 mb-1">
                                <div className="absolute inset-y-0 left-0" style={{ width: `${pqs || 0}%`, background: PQS_COLOR(pqs ?? 50) }} />
                            </div>
                            <div className="text-[10px] text-dim leading-snug">{trade.pqs_narrative || "—"}</div>
                            {regime && (
                                <div className="text-[9px] mt-1.5">
                                    <span className="text-mute">Regime: </span>
                                    <span className="font-bold" style={{ color: REGIME_COLOR[regime] || "var(--text-dim)" }}>{regime}</span>
                                </div>
                            )}
                            {trade.relative_strength_score != null && (
                                <div className="text-[9px] text-mute mt-0.5">RS: <span className="text-dim font-bold">{trade.relative_strength_score}/100</span></div>
                            )}
                        </div>

                        {/* TMS detail */}
                        <div data-testid={`tms-${trade.symbol}`}>
                            <div className="text-[9px] tracking-widest text-mute mb-0.5">TRADE MANAGEMENT AI</div>
                            <div className="px-2 py-1.5 border mb-1.5 text-center"
                                style={{ borderColor: tmsStyle.c, background: tmsStyle.bg }}>
                                <div className="text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-1" style={{ color: tmsStyle.c }}>
                                    <ArrowRight size={11} />{tmsAction}
                                </div>
                            </div>
                            {(trade.rationales || []).slice(0, 2).map((r, i) => (
                                <div key={i} className="text-[10px] text-dim leading-relaxed mb-1">{r}</div>
                            ))}
                            {trade.risk_note && (
                                <div className="text-[10px] flex items-start gap-1 mt-1.5 pt-1.5 border-t border-soft" style={{ color: "var(--amber)" }}>
                                    <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
                                    <span>{trade.risk_note}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer with notes + timestamp */}
            <div className="px-3 py-1.5 border-t border-soft text-[9px] text-mute flex items-center justify-between gap-2">
                <span className="truncate">{trade.notes || ""}</span>
                <span>Aggiornato: {trade.last_analysis_at ? new Date(trade.last_analysis_at).toLocaleString("it-IT") : "mai"}</span>
            </div>
        </div>
    );
}
